/**
 * Clickable checkboxes in rendered Markdown. The server renders "- [ ]" as a disabled checkbox;
 * mark the container that holds it and this makes the boxes live:
 *
 *   <div data-md-checklist-url="/ca/docs/api/toggleChecklistItem.php" data-md-checklist-id="4321"> ...html... </div>
 *
 * A click posts { id, index, count, checked }: index is the box's position in the container, count
 * how many boxes the page shows (the server refuses when its source disagrees, so a stale page
 * can't tick the wrong line). On a refusal the box goes back and the reason is shown.
 */

const SELECTOR = '[data-md-checklist-url]';

const boxesIn = (container: HTMLElement): HTMLInputElement[] =>
    Array.from(container.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));

const attach = (container: HTMLElement): void => {
    const boxes = boxesIn(container);
    boxes.forEach(box => {
        box.disabled = false;
        box.style.cursor = 'pointer';
    });

    container.addEventListener('change', async event => {
        const box = event.target as HTMLInputElement;
        if (box.type !== 'checkbox') {
            return;
        }
        const body = new URLSearchParams({
            id: container.dataset.mdChecklistId ?? '',
            index: String(boxes.indexOf(box)),
            count: String(boxes.length),
            checked: box.checked ? '1' : '0',
        });
        try {
            // X-Requested-With: the server skips the Tracy bar, which would corrupt the JSON.
            const response = await fetch(container.dataset.mdChecklistUrl ?? '', {
                method: 'POST',
                body,
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            const result = JSON.parse(await response.text()) as { success: string; error?: string };
            if (result.success !== 'true') {
                throw new Error(result.error ?? 'Not saved');
            }
        } catch (failure) {
            box.checked = !box.checked;
            window.alert(failure instanceof Error ? failure.message : 'Not saved');
        }
    });
};

export const initBseeMdChecklist = (): void => {
    document.querySelectorAll<HTMLElement>(SELECTOR).forEach(attach);
};

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initBseeMdChecklist());
    } else {
        initBseeMdChecklist();
    }
}
